import React, { useState, useEffect, useCallback } from 'react';
import { getPendingApprovals, approveHealer, rejectHealer } from '../../utils/api';
import { FiCheck, FiX, FiUser, FiAlertCircle } from 'react-icons/fi';

const ConfirmModal = ({ healer, onConfirm, onCancel, submitting }) => (
  <div style={S.overlay}>
    <div style={S.modal}>
      <div style={S.modalTitle}>Approve healer</div>
      <p style={S.modalBody}>
        Approve <strong>{healer.firstName} {healer.lastName}</strong> as a healer at this centre?
        Their application will move to SuperAdmin review.
      </p>
      <div style={S.modalBtns}>
        <button style={S.confirmBtn} onClick={onConfirm} disabled={submitting} type="button">
          {submitting ? 'Approving…' : 'Yes, Approve'}
        </button>
        <button style={S.cancelBtn} onClick={onCancel} disabled={submitting} type="button">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const RejectModal = ({ healer, reason, onReasonChange, onConfirm, onCancel, submitting, error }) => (
  <div style={S.overlay}>
    <div style={S.modal}>
      <div style={S.modalTitle}>Reject application</div>
      <p style={S.modalBody}>
        Reject <strong>{healer.firstName} {healer.lastName}</strong>?
        Please provide a reason — the healer will be notified via WhatsApp.
      </p>
      {error && <div style={S.inlineError}>{error}</div>}
      <textarea
        style={S.reasonInput}
        value={reason}
        onChange={e => onReasonChange(e.target.value)}
        placeholder="Reason for rejection (required)…"
        rows={3}
      />
      <div style={S.modalBtns}>
        <button style={S.rejectConfirmBtn} onClick={onConfirm} disabled={submitting} type="button">
          {submitting ? 'Rejecting…' : 'Reject Application'}
        </button>
        <button style={S.cancelBtn} onClick={onCancel} disabled={submitting} type="button">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

const PendingHealers = () => {
  const [healers,      setHealers]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [confirmId,    setConfirmId]    = useState(null);
  const [rejectId,     setRejectId]     = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError,  setRejectError]  = useState('');
  const [submitting,   setSubmitting]   = useState(false);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPendingApprovals();
      setHealers(res.data.data?.users || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load pending healers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await approveHealer(confirmId);
      setHealers(prev => prev.filter(h => h._id !== confirmId));
      setConfirmId(null);
      setSubmitting(false);
      fetchPending();
    } catch (err) {
      setError(err.response?.data?.message || 'Approval failed.');
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { setRejectError('Reason is required.'); return; }
    setSubmitting(true);
    try {
      await rejectHealer(rejectId, rejectReason.trim());
      setRejectId(null);
      setRejectReason('');
      setRejectError('');
      setSubmitting(false);
      fetchPending();
    } catch (err) {
      setRejectError(err.response?.data?.message || 'Rejection failed.');
      setSubmitting(false);
    }
  };

  const cancelConfirm = () => { setConfirmId(null); setSubmitting(false); };
  const cancelReject  = () => { setRejectId(null); setRejectReason(''); setRejectError(''); setSubmitting(false); };

  const confirmHealer = confirmId ? healers.find(h => h._id === confirmId) : null;
  const rejectHealer_ = rejectId  ? healers.find(h => h._id === rejectId)  : null;

  if (loading) return <div style={S.empty}>Loading pending healers…</div>;
  if (error)   return <div style={{ ...S.empty, color: '#e74c3c' }}><FiAlertCircle style={{ marginRight: 6 }} />{error}</div>;

  return (
    <div>
      {confirmHealer && (
        <ConfirmModal
          healer={confirmHealer} submitting={submitting}
          onConfirm={handleApprove} onCancel={cancelConfirm}
        />
      )}
      {rejectHealer_ && (
        <RejectModal
          healer={rejectHealer_} reason={rejectReason} onReasonChange={setRejectReason}
          submitting={submitting} error={rejectError}
          onConfirm={handleReject} onCancel={cancelReject}
        />
      )}

      <div style={S.card}>
        <div style={S.cardHeader}>
          Pending Healers ({healers.length})
        </div>
        {healers.length === 0 ? (
          <div style={S.empty}>No pending healer applications.</div>
        ) : (
          <div>
            {healers.map(h => (
              <div key={h._id} style={S.healerCard}>
                <div style={S.avatarRow}>
                  <div style={S.avatar}>
                    {h.firstName?.charAt(0)}{h.lastName?.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={S.healerName}>{h.firstName} {h.lastName}</div>
                    <div style={S.healerEmail}>{h.email}</div>
                  </div>
                  <span style={h.registrationSource === 'invite' ? S.inviteBadge : S.openBadge}>
                    {h.registrationSource === 'invite' ? 'Invited' : 'Open'}
                  </span>
                </div>
                <div style={S.metaRow}>
                  <span style={S.meta}><FiUser size={11} style={{ marginRight: 4 }} />
                    {h.yearsExperience ? `${h.yearsExperience} yrs experience` : 'Experience not stated'}
                  </span>
                  <span style={S.meta}>
                    Registered {h.createdAt ? new Date(h.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </span>
                </div>
                {h.certifications?.length > 0 && (
                  <div style={S.certs}>
                    {h.certifications.map((c, i) => (
                      <span key={i} style={S.certBadge}>{c.name || c}</span>
                    ))}
                  </div>
                )}
                <div style={S.statusRow}>
                  <span style={S.statusPill}>Pending your approval</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      style={S.approveBtn} type="button"
                      onClick={() => setConfirmId(h._id)}
                    >
                      <FiCheck size={13} style={{ marginRight: 5 }} />Approve
                    </button>
                    <button
                      style={S.rejectBtn} type="button"
                      onClick={() => { setRejectId(h._id); setRejectError(''); setRejectReason(''); }}
                    >
                      <FiX size={13} style={{ marginRight: 5 }} />Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const S = {
  card:            { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' },
  cardHeader:      { padding: '16px 24px', borderBottom: '1px solid #e0e0e0', fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  empty:           { padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  healerCard:      { padding: '18px 24px', borderBottom: '1px solid #f5f5f5' },
  avatarRow:       { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  avatar:          { width: '40px', height: '40px', borderRadius: '50%', background: '#d4ac0d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', flexShrink: 0 },
  healerName:      { fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '700', color: '#1a1a2e' },
  healerEmail:     { fontSize: '12px', color: '#888', marginTop: '2px' },
  inviteBadge:     { fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px', background: '#eafaf1', color: '#27ae60', fontFamily: "'Raleway', sans-serif" },
  openBadge:       { fontSize: '10px', fontWeight: '700', padding: '3px 10px', borderRadius: '12px', background: '#eaf4fb', color: '#2980b9', fontFamily: "'Raleway', sans-serif" },
  metaRow:         { display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' },
  meta:            { display: 'inline-flex', alignItems: 'center', fontSize: '12px', color: '#666' },
  certs:           { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' },
  certBadge:       { fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: '#fef9e7', color: '#7d6608', border: '1px solid #f9e79f', fontFamily: "'Raleway', sans-serif", fontWeight: '600' },
  statusRow:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  statusPill:      { fontSize: '11px', color: '#e67e22', background: 'rgba(230,126,34,0.1)', padding: '4px 12px', borderRadius: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif" },
  approveBtn:      { display: 'inline-flex', alignItems: 'center', padding: '7px 14px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  rejectBtn:       { display: 'inline-flex', alignItems: 'center', padding: '7px 14px', background: 'transparent', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal:           { background: '#fff', borderRadius: '10px', padding: '32px', width: '100%', maxWidth: '420px' },
  modalTitle:      { fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginBottom: '12px' },
  modalBody:       { fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '16px' },
  reasonInput:     { width: '100%', padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '14px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', resize: 'vertical', marginBottom: '4px' },
  inlineError:     { color: '#e74c3c', fontSize: '12px', marginBottom: '10px' },
  modalBtns:       { display: 'flex', gap: '10px', marginTop: '16px' },
  confirmBtn:      { padding: '10px 20px', background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  rejectConfirmBtn:{ padding: '10px 20px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn:       { padding: '10px 20px', background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '6px', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
};

export default PendingHealers;
